import { makeAutoObservable, runInAction } from 'mobx'
import { hrEmployeesService, type HREmployee } from '@/services/hr/hrEmployees.service'
import {
  branchesService,
  categoriesService,
  clientTypesService,
  countriesService,
  currenciesService,
  legalEntitiesService,
  locationsService,
  packagesService,
  partnersService,
  procurementContainerTypesService,
  procurementBatchesService,
  procurementPlansService,
  procurementStatusTemplatesService,
  procurementTermsService,
  productGroupsService,
  productsService,
  rolesService,
  usersService,
  warehousesService,
} from '@/services'
import type {
  Branch,
  Category,
  ClientType,
  Country,
  Currency,
  LegalEntity,
  Location,
  Package,
  Partner,
  ProcurementContainerType,
  ProcurementBatch,
  ProcurementPlan,
  ProcurementProforma,
  ProcurementStatusTemplate,
  ProcurementTerm,
  Product,
  ProductGroup,
  Role,
  UserIdentity,
  Warehouse,
} from '@/types/api'
import type { RootStore } from './RootStore'

/**
 * Shared, deduplicated reference data for selects and id→name resolution.
 * Reference catalogs are small; one bounded page (the backend maximum) covers
 * them, so tables never issue one request per row.
 */
type LookupMap = {
  branches: Branch
  countries: Country
  currencies: Currency
  locations: Location
  categories: Category
  packages: Package
  products: Product
  productGroups: ProductGroup
  partners: Partner
  legalEntities: LegalEntity
  procurementTerms: ProcurementTerm
  procurementContainerTypes: ProcurementContainerType
  procurementBatches: ProcurementBatch
  procurementStatusTemplates: ProcurementStatusTemplate
  procurementPlans: ProcurementPlan
  procurementProformas: ProcurementProforma
  roles: Role
  clientTypes: ClientType
  users: UserIdentity
  /** Staff logins only, as Ucode's `employee` table: no client or counterparty accounts. */
  staff: UserIdentity
  hrEmployees: HREmployee
  warehouses: Warehouse
}

export type LookupKey = keyof LookupMap

const sources: { [K in LookupKey]: { capability: string; load: () => Promise<LookupMap[K][]>; id: (item: LookupMap[K]) => string } } = {
  branches: { capability: 'branches.read', load: () => branchesService.list({ limit: 1000 }).then((r) => r.items), id: (i) => i.id },
  countries: { capability: 'countries.read', load: () => countriesService.list({ limit: 1000 }).then((r) => r.items), id: (i) => i.id },
  currencies: { capability: 'currencies.read', load: () => currenciesService.list({ limit: 1000 }).then((r) => r.items), id: (i) => i.id },
  locations: { capability: 'locations.read', load: () => locationsService.list({ limit: 1000 }).then((r) => r.items), id: (i) => i.id },
  categories: { capability: 'categories.read', load: () => categoriesService.list({ limit: 1000, sort: [{ field: 'sort_order', type: 'asc' }] }).then((r) => r.items), id: (i) => i.id },
  packages: { capability: 'packages.read', load: () => packagesService.list({ limit: 1000 }).then((r) => r.items), id: (i) => i.id },
  products: { capability: 'products.read', load: () => productsService.list({ limit: 1000 }).then((r) => r.items), id: (i) => i.id },
  productGroups: { capability: 'product_groups.read', load: () => productGroupsService.list({ limit: 1000 }).then((r) => r.items), id: (i) => i.id },
  partners: { capability: 'partners.read', load: () => partnersService.list({ limit: 1000 }).then((r) => r.items), id: (i) => i.id },
  legalEntities: { capability: 'legal_entities.read', load: () => legalEntitiesService.list({ limit: 1000 }).then((r) => r.items), id: (i) => i.id },
  procurementTerms: { capability: 'procurement_terms.read', load: () => procurementTermsService.list({ limit: 1000 }).then((r) => r.items), id: (i) => i.id },
  procurementContainerTypes: { capability: 'procurement_container_types.read', load: () => procurementContainerTypesService.list({ limit: 1000 }).then((r) => r.items), id: (i) => i.id },
  procurementBatches: { capability: 'procurement_batches.read', load: () => procurementBatchesService.list({ limit: 1000, sort: [{ field: 'batch_date', type: 'desc' }] }).then((r) => r.items), id: (i) => i.id },
  procurementStatusTemplates: { capability: 'procurement_status_templates.read', load: () => procurementStatusTemplatesService.list({ limit: 1000, sort: [{ field: 'order_number', type: 'asc' }] }).then((r) => r.items), id: (i) => i.id },
  procurementPlans: { capability: 'procurement_plans.read', load: () => procurementPlansService.list({ limit: 1000, sort: [{ field: 'created_date', type: 'desc' }] }).then((r) => r.items), id: (i) => i.id },
  procurementProformas: { capability: 'procurement_proformas.read', load: () => procurementBatchesService.proformaOptions(false).then((r) => r.items), id: (i) => i.id },
  roles: { capability: 'roles.read', load: () => rolesService.list({ limit: 1000 }).then((r) => r.items), id: (i) => i.guid },
  clientTypes: { capability: 'client_types.read', load: () => clientTypesService.list({ limit: 1000 }).then((r) => r.items), id: (i) => i.guid },
  warehouses: { capability: 'warehouses.read', load: () => warehousesService.list({ limit: 1000, sort: [{ field: 'name', type: 'asc' }] }).then((r) => r.items), id: (i) => i.id },
  users: { capability: 'users.read', load: () => usersService.list({ limit: 1000, sort: [{ field: 'display_name', type: 'asc' }] }).then((r) => r.items), id: (i) => i.id },
  hrEmployees: { capability: 'hr_employees.read', load: () => hrEmployeesService.list({ limit: 1000, sort: [{ field: 'full_name', type: 'asc' }] }).then((r) => r.items), id: (i) => i.id },
  staff: {
    capability: 'users.read',
    load: () => usersService.list({ limit: 1000, filters: [{ field: 'client_type.code', type: 'not_in', values: ['client', 'counterparty'] }], sort: [{ field: 'display_name', type: 'asc' }] }).then((r) => r.items),
    id: (i) => i.id,
  },
}

type Entry = { items: unknown[]; loaded: boolean; loading: boolean; failed: boolean }

export class LookupStore {
  private entries = new Map<LookupKey, Entry>()
  private inflight = new Map<LookupKey, Promise<void>>()
  private readonly root: RootStore

  constructor(root: RootStore) {
    this.root = root
    makeAutoObservable<LookupStore, 'inflight' | 'root'>(this, { inflight: false, root: false })
  }

  items<K extends LookupKey>(key: K): LookupMap[K][] {
    return (this.entries.get(key)?.items as LookupMap[K][]) ?? []
  }

  isLoading(key: LookupKey) {
    return this.entries.get(key)?.loading ?? false
  }

  canUse(key: LookupKey) {
    return this.root.session.can(sources[key].capability)
  }

  byId<K extends LookupKey>(key: K, id?: string | null): LookupMap[K] | undefined {
    if (!id) return undefined
    const source = sources[key]
    return this.items(key).find((item) => source.id(item) === id)
  }

  ensure(key: LookupKey): Promise<void> {
    const entry = this.entries.get(key)
    if (entry?.loaded || !this.canUse(key)) return Promise.resolve()
    const pending = this.inflight.get(key)
    if (pending) return pending

    this.entries.set(key, { items: entry?.items ?? [], loaded: false, loading: true, failed: false })
    const promise = (sources[key].load() as Promise<unknown[]>)
      .then((items) => {
        runInAction(() => this.entries.set(key, { items, loaded: true, loading: false, failed: false }))
      })
      .catch(() => {
        runInAction(() => this.entries.set(key, { items: [], loaded: false, loading: false, failed: true }))
      })
      .finally(() => this.inflight.delete(key))
    this.inflight.set(key, promise)
    return promise
  }

  /** Mark stale after a mutation; the next consumer refetches. */
  invalidate(key: LookupKey) {
    const entry = this.entries.get(key)
    if (entry) this.entries.set(key, { ...entry, loaded: false })
    void this.ensure(key)
  }

  /** «Обновить» in the top bar: every list loaded so far is fetched again; its items stay until the new ones arrive. */
  refreshAll(): Promise<void> {
    const pending: Promise<void>[] = []
    for (const [key, entry] of this.entries) {
      if (!entry.loaded && !entry.failed) continue
      this.entries.set(key, { ...entry, loaded: false })
      pending.push(this.ensure(key))
    }
    return Promise.all(pending).then(() => undefined)
  }

  clear() {
    this.entries.clear()
    this.inflight.clear()
  }
}
